/**
 * @aiPurpose shared TanStack-Query subscription for the per-user
 *   notification preferences row (#1068, #1069). Loaded once on app
 *   mount by `RealtimeBootstrap` so the sound-playback path (#1073)
 *   has the row available when a `notifications:updated` event
 *   arrives without requiring a settings-page visit.
 * @aiInvariant the cache key `NOTIFICATION_PREFS_QK` is exported so
 *   the settings panel and the realtime handler read/write the same
 *   slot — mutating the row in settings updates the sound-key in
 *   realtime without a refetch.
 */
import { useQuery } from '@tanstack/react-query';
import type { NotificationPreferences } from '@magimp/shared';
import { getNotificationPreferences } from '../../../api/notifications.js';

export const NOTIFICATION_PREFS_QK = ['notification-preferences'] as const;

export function useNotificationPreferences() {
  return useQuery<NotificationPreferences>({
    queryKey: NOTIFICATION_PREFS_QK,
    queryFn: getNotificationPreferences,
    // WHY: preferences change only via the settings panel which
    //   writes the cache directly on success. A long staleTime
    //   spares network on every page navigation.
    staleTime: 5 * 60_000,
  });
}
